import React, { useState } from 'react';
import { Typography, Card, Form, Input, Button, message, Spin, Row, Col, InputNumber, Alert, Select } from 'antd';
import { useMutation } from 'react-query';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import molecularInteractionService, { 
  RNAHybridRequest, 
  RNAHybridResponse, 
  RNAHybridResult
} from '../../api/molecularInteractionService';
import RNAhybridResult from '../../components/results/interaction/RNAhybridResult';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function RNAhybridPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [results, setResults] = useState<RNAHybridResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  // RNAhybrid分析mutation
  const { mutate: runRNAHybrid, isLoading: isRunning } = useMutation(
    (request: RNAHybridRequest) => molecularInteractionService.runRNAhybrid(request),
    {
      onSuccess: (response: RNAHybridResponse) => {
        if (response.results?.length > 0) {
          setResults(response.results);
          setError(null);
          message.success('RNAhybrid分析完成');
        } else {
          setError('分析失败');
          setResults([]);
        }
      },
      onError: (error: any) => {
        console.error('RNAhybrid分析失败:', error);
        setError(error.response?.data?.detail || error.message || '分析失败');
        setResults([]);
      }
    }
  );

  const handleSubmit = () => {
    form.validateFields().then(values => {
      setError(null);
      runRNAHybrid(values);
    });
  };

  // 渲染结果的函数 - 使用新的RNAhybridResult组件
  const renderResults = () => {
    if (!results || results.length === 0) {
      return <Paragraph>暂无结果</Paragraph>;
    }

    // 构造组件需要的数据格式，包含参数信息
    const formValues = form.getFieldsValue();
    const resultData = {
      results: results,
      parameters: formValues // 传递表单参数
    };

    return <RNAhybridResult data={resultData} />;
  };

  return (

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/interaction')}
            style={{ marginBottom: 16 }}
          >
            返回分子互作分析
          </Button>
          
          <Typography>
            <Title level={2}>RNAhybrid miRNA靶标预测</Title>
            <Paragraph>
              RNAhybrid是一个用于miRNA与mRNA靶标结合位点预测的工具。请输入miRNA序列和靶标mRNA序列进行分析。
            </Paragraph>
          </Typography>
        </div>

        <Card>
          <Form form={form} layout="vertical">
            {/* 从原页面复制表单内容 */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="mirna_sequence"
                  label="miRNA序列"
                  rules={[{ required: true, message: '请输入miRNA序列' }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder="请输入miRNA序列（FASTA格式或纯序列）" 
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="target_sequence"
                  label="靶标mRNA序列"
                  rules={[{ required: true, message: '请输入靶标mRNA序列' }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder="请输入靶标mRNA序列（FASTA格式或纯序列）" 
                  />
                </Form.Item>
              </Col>
            </Row>
            
            {/* 其他参数设置 */}
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="database"
                  label="数据库"
                  initialValue="3utr_human"
                >
                  <Select>
                    <Option value="3utr_human">Human</Option>
                    <Option value="3utr_fly">Fly</Option>
                    <Option value="3utr_worm">Worm</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="hits_per_target"
                  label="每个靶标结果数"
                  initialValue={2}
                >
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="max_target_length"
                  label="最大靶标长度"
                  initialValue={10000}
                >
                  <InputNumber min={50} max={10000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item>
              <Button 
                type="primary"
                onClick={handleSubmit}
                loading={isRunning}
                size="large"
              >
                开始分析
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {isRunning && (
          <div style={{ textAlign: 'center', margin: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>正在进行RNAhybrid分析，请稍候...</div>
          </div>
        )}

        {error && (
          <Alert
            message="分析错误"
            description={error}
            type="error"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}

        {results && results.length > 0 && (
          <div style={{ marginTop: 24 }}>
            {renderResults()}
          </div>
        )}
      </div>
  );
}